<template>
	<div class="tabs">
		<div class="tabs-nav">
			<nav class="flex space-x-4" :class="navClasses" aria-label="Tabs" data-testid="tab-nav">
				<button
					v-for="tab in nav"
					:key="tab.name"
					:disabled="tab.disabled"
					data-testid="tab"
					:class="[
						tab.id == selected
							? theme == 'light'
								? 'text-mabey-950'
								: 'text-white'
							: 'text-gray-400',
						tab.disabled ? 'opacity-25 cursor-not-allowed' : 'opacity-100',
						tab.id != selected && !tab.disabled
							? theme == 'light'
								? 'hover:text-gray-900'
								: 'opacity-50 hover:text-white'
							: '',
						tabClasses
					]"
					:aria-current="tab.id == selected ? 'page' : undefined"
					@click="selected = tab.id">
					<app-loader
						v-if="tab.loading"
						:theme="theme == 'light' ? 'dark' : 'light'"
						class="ml-0 mr-0" />

					<span
						v-else
						class="flex"
						:class="!tab.inline ? 'flex-col' : 'items-center justify-center gap-3'">
						<app-icon
							v-if="tab.icon"
							:icon="tab.icon"
							:class="tab.iconClasses ?? 'text-4xl mb-2'" />

						{{ tab.name }}
					</span>
				</button>
			</nav>
		</div>

		<slot></slot>

		<div v-if="nav" class="tabs-content pt-4">
			<div
				v-for="(tab, index) in nav"
				:id="tab.id"
				:key="index"
				data-testid="tab-content"
				:class="[
					tab.id == selected ? 'opacity-100 h-auto' : 'h-0 invisible opacity-0 absolute'
				]">
				<slot
					v-if="!lazyLoad || (lazyLoad && tab.id == selected)"
					:name="`tab${index + 1}`">
				</slot>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted } from 'vue'

	export interface Tab {
		id: string
		name: string
		icon?: Array<string>
		iconClasses: string
		inline: boolean
		disabled?: boolean
		loading?: boolean
	}

	const props = withDefaults(
		defineProps<{
			name?: string
			nav?: Array<Tab>
			justify?: boolean
			lazyLoad?: boolean
			iconSize?: string
			theme?: 'light' | 'dark'
		}>(),
		{
			name: 'tabs',
			nav: undefined,
			iconSize: 'large',
			theme: 'light'
		}
	)

	const selected = ref()

	const navClasses = computed(() => ({
		'justify-evenly': props.justify
	}))

	const tabClasses = computed(() => ({
		'rounded-md px-3 py-2 text-sm font-medium shadow': true,
		'w-full': props.justify,
		'bg-white': props.theme == 'light',
		'bg-mabey-950': props.theme == 'dark'
	}))

	onMounted(() => {
		if (props.nav) {
			selected.value = props.nav[0].id
		}
	})
</script>
